import React, { useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Button } from "@shared/ui/button";
import { Label } from "@shared/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { useDepartmentOptions, useRoleOptions } from "@features/onboarding/utility-setup/hooks";
import { userFilters } from "../../../types";

interface FilterPanelProps {
  currentFilters: userFilters;
  onApplyFilters: (filters: Partial<userFilters>) => void;
  onResetFilters: () => void;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  currentFilters,
  onApplyFilters,
  onResetFilters,
}) => {
  // Use the same hooks as UserForm
  const { data: departmentData, isLoading: isDeptLoading } = useDepartmentOptions();

  const form = useForm({
    defaultValues: {
      department: currentFilters.department || 'ALL_DEPARTMENTS',
      role: currentFilters.role || 'ALL_ROLES',
    },
  });

  const selectedDepartment = form.watch('department');

  // Get department code for the selected department
  const selectedDepartmentCode = useMemo(() => {
    if (!selectedDepartment || selectedDepartment === 'ALL_DEPARTMENTS' || !departmentData) {
      return [];
    }
    
    // Find by name since selectedDepartment now holds the name
    const dept = departmentData.find((d: any) => d.name === selectedDepartment);
    return dept?.code ? [dept.code] : [];
  }, [selectedDepartment, departmentData]);

  // Fetch roles for the selected department
  const { data: roleData, isLoading: isRoleLoading } = useRoleOptions(selectedDepartmentCode);

  // Transform department data to options
  const departmentOptions = useMemo(() => {
    return (
      departmentData?.map((dept: any) => ({
        value: dept.name,
        label: dept.name,
        code: dept.code,
      })) ?? []
    );
  }, [departmentData]);

  // Get role options for the selected department
  const   roleOptions = useMemo(() => {
    if (!roleData || selectedDepartmentCode.length === 0) {
      return [];
    }
    
    const roles: Array<{ value: string; label: string }> = [];
    
    roleData.forEach((dept) => {
      const deptCode = dept.department || dept.code;
      
      if (selectedDepartmentCode.includes(deptCode)) {
        dept.roles?.forEach((role: any) => {
          if (!roles.some(r => r.value === role.roleName)) {
            roles.push({
              value: role.roleName,
              label: role.roleName,
            });
          }
        });
      }
    });
    
    return roles;
  }, [roleData, selectedDepartmentCode]);

  // Sync form with current filters from URL
  useEffect(() => {
    form.setValue('department', currentFilters.department || 'ALL_DEPARTMENTS');
    form.setValue('role', currentFilters.role || 'ALL_ROLES');
  }, [currentFilters, form]);

  // Reset role when department changes
  useEffect(() => {
    if (selectedDepartment === 'ALL_DEPARTMENTS') {
      form.setValue('role', 'ALL_ROLES');
    } else {
      // If current role is not in the new role options, reset it
      const currentRole = form.getValues('role');
      if (currentRole !== 'ALL_ROLES' && !roleOptions.some(r => r.value === currentRole)) {
        form.setValue('role', 'ALL_ROLES');
      }
    }
  }, [selectedDepartment, roleOptions, form]);

  const handleApplyFilters = (data: any) => {
    const filtersToApply: Partial<userFilters> = {};

    if (data.department && data.department !== 'ALL_DEPARTMENTS') {
      filtersToApply.department = data.department;
    }

    if (data.role && data.role !== 'ALL_ROLES') {
      filtersToApply.role = data.role;
    }

    console.log('filters to apply', filtersToApply);
    onApplyFilters(filtersToApply);
  };

  const handleResetFilters = () => {
    form.reset({
      department: 'ALL_DEPARTMENTS',
      role: 'ALL_ROLES',
    });
    onResetFilters();
  };

  const isRoleDisabled = 
    selectedDepartment === 'ALL_DEPARTMENTS' || 
    selectedDepartmentCode.length === 0 || 
    isRoleLoading;

  return (
    <form onSubmit={form.handleSubmit(handleApplyFilters)}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>Department</Label>
          <Controller
            name="department"
            control={form.control}
            render={({ field }) => (
              <Select 
                value={field.value} 
                onValueChange={field.onChange}
                disabled={isDeptLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="ALL_DEPARTMENTS">All Departments</SelectItem>
                    {departmentOptions?.map((dept) => (
                      <SelectItem key={dept.code} value={dept.value}>
                        {dept.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="space-y-2">
          <Label>Role</Label>
          <Controller
            name="role"
            control={form.control}
            render={({ field }) => (
              <Select 
                value={field.value} 
                onValueChange={field.onChange}
                disabled={isRoleDisabled}
              >
                <SelectTrigger>
                  <SelectValue 
                    placeholder={
                      selectedDepartment === 'ALL_DEPARTMENTS'
                        ? "Select department first"
                        : "Select role"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="ALL_ROLES">All Roles</SelectItem>
                    {roleOptions?.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="col-span-1 md:col-span-2 lg:col-span-2 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleResetFilters}>
            Reset Filters
          </Button>
          <Button type="submit" disabled={isDeptLoading}>
            Apply Filters
          </Button>
        </div>
      </div>
    </form>
  );
};